import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ClipboardCheck, ChevronDown, ChevronRight, Sparkles, X, Plus } from 'lucide-react';
import { Input } from '../ui/input';

/**
 * OutputsHeaderRow — Feb 27, 2026 (v6.8.14)
 *
 * Provider redesign (round 3):
 *   "ROS is automatic — when would you NOT use ROS? Remove it.
 *    PE has to always be filled out — it's just remarkable or
 *    unremarkable. Default is unremarkable; check 'Except for' to
 *    open a free-text box for the specific findings."
 *   "And the imaging/labs/referrals/letter are SEPARATE documents,
 *    not part of the note — distinguish those visually."
 *
 * Result:
 *   • ROS toggle REMOVED (always implicit, backend defaults true)
 *   • PE toggle REMOVED — replaced with a single "PE unremarkable
 *     [Except for ↧]" control. PE is always generated.
 *   • Two groups: "Note" (PE-Except control) on the left, "Documents"
 *     (Imaging / Meds / Labs / Referrals / Profile / Letter / E/M)
 *     on the right with a thin divider.
 *
 * PE wording for the unremarkable default is observational-only (no
 * claimed hands-on findings) — works for in-person AND virtual visits.
 * Backend (`prompts/clinical_note.py`) enforces the wording.
 *
 * Testids preserved where present (toggle-imaging, toggle-meds, etc.).
 * New testids: pe-except-toggle, pe-except-input.
 */
const DocItem = ({ id, testId, checked, onChange, label, accent = 'emerald' }) => {
  const tints = {
    violet: 'bg-violet-500/15 border-violet-400/50 text-violet-100',
    rose: 'bg-rose-500/15 border-rose-400/50 text-rose-100',
    sky: 'bg-sky-500/15 border-sky-400/50 text-sky-100',
    amber: 'bg-amber-500/15 border-amber-400/50 text-amber-100',
    teal: 'bg-teal-500/15 border-teal-400/50 text-teal-100',
    yellow: 'bg-yellow-500/15 border-yellow-400/50 text-yellow-100',
    cyan: 'bg-cyan-500/15 border-cyan-400/50 text-cyan-100',
  };
  const onCls = tints[accent] || tints.violet;
  return (
    <label
      htmlFor={id}
      className={`flex items-center gap-1 px-1.5 py-0.5 rounded border cursor-pointer select-none transition-colors ${
        checked ? onCls : 'bg-transparent border-border/60 text-foreground/65 hover:text-foreground hover:border-border'
      }`}
    >
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="w-3 h-3 cursor-pointer" data-testid={testId} />
      <span className="text-[11px] font-semibold tracking-tight">{label}</span>
    </label>
  );
};

export const OutputsHeaderRow = ({
  // Note-section state (PE only — ROS is implicit)
  peUnremarkable, setPeUnremarkable, // currently locked true; kept for future "remarkable" mode
  peExceptions, setPeExceptions,
  markPeFindingsManual, // from useAutoDetect — call to lock manual edits
  // Documents-section state
  imagingOrders, showImagingSection, setShowImagingSection,
  addImaging, setImagingOrders,
  medsRxd, setMedsRxd,
  showLabsSection, setShowLabsSection, setLabOrders,
  specialties, showReferralsSection, setShowReferralsSection,
  addSpecialty, setSpecialties,
  generateLimitations, setGenerateLimitations,
  generateAdminLetter, setGenerateAdminLetter,
  letterType, setLetterType, setCustomLetterDescription,
  includeEmAnalysis, setIncludeEmAnalysis,
  // Mar 1, 2026 — Compact ICD-10 quick-add chip lives at the right edge
  // of the Separate Documents chip row (provider drawing). Mirrors the
  // primary ICD-10 input below the notes but lets the provider add codes
  // without scrolling away from the demographic/intake area.
  manualIcd10, setManualIcd10,
}) => {
  // Feb 27, 2026 (v6.8.23) — TYPING-LAG FIX for PE Except-for input.
  // The input was bound to App.js `peExceptions` state. Every keystroke
  // triggered a top-level re-render that rippled through OutputDocsPanel,
  // ProviderToggleSection, etc. Local-state buffer + 250ms debounced
  // commit keeps the input keystroke-snappy; parent state catches up on
  // pause. Auto-detect writes (from useAutoDetect) bypass via the
  // explicit `peExceptions` prop changing (length grew) — we keep the
  // local mirror in sync.
  const [peLocal, setPeLocal] = useState(peExceptions || '');
  const peDebounceRef = useRef(null);
  useEffect(() => {
    // Sync down from parent only when the value diverges AND the
    // divergence didn't originate locally (i.e., auto-detect/extractor).
    if ((peExceptions || '') !== peLocal && !peDebounceRef.current) {
      setPeLocal(peExceptions || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peExceptions]);
  const commitPeUpstream = useCallback((val) => {
    if (peDebounceRef.current) clearTimeout(peDebounceRef.current);
    peDebounceRef.current = setTimeout(() => {
      setPeExceptions(val);
      peDebounceRef.current = null;
    }, 250);
  }, [setPeExceptions]);

  // Local-only — controls visibility of the "Except for" free-text input.
  // Reflects the truthiness of peExceptions: if there's any text, expand;
  // otherwise the provider can click the chip to open it.
  const [peExceptExpanded, setPeExceptExpanded] = useState(
    !!(peExceptions && peExceptions.trim().length > 0)
  );
  const peExceptOn = peExceptExpanded || !!(peExceptions && peExceptions.trim().length > 0);

  // Feb 27, 2026 (v6.8.15) — Auto-detect handshake with useAutoDetect's
  // extractPEFindings(). When the extractor populates peExceptions from
  // dictation while the toggle is collapsed, we auto-expand and flag the
  // value as "auto-detected" so the provider knows where it came from
  // and can dismiss in one click. We treat ANY change that wasn't started
  // by the user clicking the toggle as auto-detected (the autoDetect hook
  // is the only other writer).
  const userTouchedRef = useRef(false);
  const lastSeenLengthRef = useRef((peExceptions || '').length);
  const [autoDetected, setAutoDetected] = useState(false);

  useEffect(() => {
    const curLen = (peExceptions || '').length;
    const prevLen = lastSeenLengthRef.current;
    lastSeenLengthRef.current = curLen;
    // External writer added content while the user wasn't typing
    if (curLen > prevLen && !userTouchedRef.current) {
      if (!peExceptExpanded) setPeExceptExpanded(true);
      setAutoDetected(true);
    }
    if (curLen === 0) setAutoDetected(false);
  }, [peExceptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const imagingOn = imagingOrders.length > 0 || showImagingSection;
  const referralsOn = specialties.length > 0 || showReferralsSection;
  const letterOn = generateAdminLetter || !!letterType;
  const docsActiveCount = [
    imagingOn, medsRxd, showLabsSection, referralsOn,
    generateLimitations, letterOn, includeEmAnalysis,
  ].filter(Boolean).length;

  return (
    <div
      // Mar 2, 2026 — Sticky offset was `top-[124px]` which sat 16px
      // above the fixed HeaderToolbar's bottom edge, letting the header
      // paint over this row on scroll. Bumped to `top-[140px]` to match
      // the <main> pt-[140px] clearance on lg. z-index dropped from 30
      // to 20 (below the sticky demographic bar at z-30).
      className="sticky top-[112px] z-30 mb-3 lg:mb-4 rounded-lg border border-border bg-card/95 backdrop-blur-md shadow-md px-3 py-2"
      data-testid="outputs-header-row"
    >
      {/* Mar 1, 2026 — Single tight row: NOTE group + DIVIDER + DOCS chips
          all on one natural line (provider explicitly wanted no big gap
          between Note and Separate Documents). The ICD-10 quick-add bar
          appears as its own row BELOW, width-constrained to the right
          half so it aligns with the Smart ICD-10 Search box underneath. */}
      <div className="flex items-start gap-3 flex-wrap">
        {/* ───────────────────────── NOTE GROUP ───────────────────────── */}
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <ClipboardCheck className="w-3 h-3 text-emerald-400" />
            <span className="elegant-label text-[10px]">Note</span>
            <span className="text-[9px] italic text-muted-foreground/60">HPI · ROS · A/P always included</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* PE Unremarkable — always-on indicator (visual reassurance) */}
            <span
              className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-400/40 bg-emerald-500/12 text-emerald-200 text-[11px] font-semibold"
              title="PE Unremarkable boilerplate is always generated (observational only). Click 'Except for' to add specific findings."
              data-testid="pe-always-pill"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              PE Unremarkable
            </span>
            {/* Except-for toggle — opens the free-text box */}
            <button
              type="button"
              onClick={() => {
                const nextExpanded = !peExceptExpanded;
                setPeExceptExpanded(nextExpanded);
                if (!nextExpanded && setPeExceptions) setPeExceptions('');
              }}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded border cursor-pointer select-none transition-colors text-[11px] font-semibold ${
                peExceptOn
                  ? 'bg-amber-500/15 border-amber-400/50 text-amber-100'
                  : 'bg-transparent border-border/60 text-foreground/65 hover:text-foreground hover:border-border'
              }`}
              data-testid="pe-except-toggle"
              aria-expanded={peExceptOn}
            >
              {peExceptOn ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              Except for…
              {peExceptions && peExceptions.trim().length > 0 && (
                <span className="ml-0.5 text-[9px] bg-amber-500/30 text-amber-100 rounded px-1 tabular-nums">
                  {peExceptions.trim().split(/[,;\n]/).filter(s => s.trim()).length}
                </span>
              )}
            </button>
          </div>
          {peExceptOn && (
            <div className="flex items-center gap-1 mt-0.5">
              {autoDetected && (
                <span
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-fuchsia-500/20 border border-fuchsia-400/50 text-fuchsia-100 text-[10px] font-bold shrink-0 animate-pulse"
                  title="Findings auto-detected from your dictation. Edit or clear if not accurate."
                  data-testid="pe-except-auto-badge"
                >
                  <Sparkles className="w-2.5 h-2.5" />
                  AUTO
                </span>
              )}
              <Input
                value={peLocal}
                onChange={(e) => {
                  const v = e.target.value;
                  userTouchedRef.current = true;
                  setAutoDetected(false);
                  if (markPeFindingsManual) markPeFindingsManual();
                  setPeLocal(v);            // urgent — keystroke stays snappy
                  commitPeUpstream(v);      // debounced — parent catches up in 250ms
                }}
                placeholder="e.g., tenderness L4-L5, decreased ROM flexion 40°, +SLR right (comma- or newline-separated)"
                className={`h-7 text-[11px] bg-input/60 focus:ring-amber-500/20 flex-1 ${
                  autoDetected
                    ? 'border-fuchsia-500/50 focus:border-fuchsia-500/80'
                    : 'border-amber-500/40 focus:border-amber-500/70'
                }`}
                data-testid="pe-except-input"
                autoFocus={peExceptExpanded && !(peExceptions && peExceptions.length > 0)}
              />
              {peExceptions && peExceptions.trim().length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    userTouchedRef.current = true;
                    setAutoDetected(false);
                    if (markPeFindingsManual) markPeFindingsManual();
                    setPeLocal('');
                    setPeExceptions('');
                    if (peDebounceRef.current) { clearTimeout(peDebounceRef.current); peDebounceRef.current = null; }
                  }}
                  className="h-7 w-7 flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                  title={autoDetected ? "Dismiss auto-detected findings" : "Clear exceptions"}
                  data-testid="pe-except-clear"
                  aria-label="Clear PE exceptions"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* ───────────── DIVIDER ───────────── */}
        <div className="hidden md:block w-px h-12 bg-border/60 self-center shrink-0" aria-hidden="true" />

        {/* ──────────────────────── DOCUMENTS GROUP ──────────────────────── */}
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <ClipboardCheck className="w-3 h-3 text-amber-400" />
            <span className="elegant-label text-[10px]">
              Separate Documents
            </span>
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 tabular-nums"
              data-testid="outputs-header-count"
            >
              {docsActiveCount}
            </span>
            <span className="text-[9px] italic text-muted-foreground/60 hidden sm:inline">
              fresh drafts, not part of the note
            </span>
          </div>
          <div className="flex items-center gap-1 flex-wrap" data-testid="outputs-header-items">
            <DocItem id="hdr-toggle-imaging" testId="toggle-imaging" checked={imagingOn}
              onChange={(checked) => { setShowImagingSection(checked); if (checked && imagingOrders.length === 0) addImaging(); if (!checked && setImagingOrders) setImagingOrders([]); }}
              label="Imaging" accent="violet" />
            <DocItem id="hdr-toggle-meds" testId="toggle-meds" checked={medsRxd}
              onChange={setMedsRxd} label="Meds Rx'd" accent="rose" />
            <DocItem id="hdr-toggle-labs" testId="toggle-labs" checked={showLabsSection}
              onChange={(checked) => { setShowLabsSection(checked); if (!checked && setLabOrders) setLabOrders(''); }}
              label="Labs" accent="sky" />
            <DocItem id="hdr-toggle-referrals" testId="toggle-referrals" checked={referralsOn}
              onChange={(checked) => {
                setShowReferralsSection(checked);
                if (checked && specialties.length === 0) addSpecialty();
                if (!checked && setSpecialties) setSpecialties([]);
                // Feb 27, 2026 (v6.8.24) — Auto-scroll specialty input
                // into view when toggled on. Provider feedback: "the
                // specialty box is all the way below the input" — they
                // didn't see where to fill it. Slight delay so the DOM
                // has time to render the input.
                if (checked) {
                  setTimeout(() => {
                    const target = document.querySelector('[data-testid="add-specialty-btn"]')
                      || document.querySelector('[data-testid^="specialty-select-"]');
                    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 120);
                }
              }}
              label="Referrals" accent="amber" />
            <DocItem id="hdr-toggle-profile" testId="toggle-profile" checked={generateLimitations}
              onChange={setGenerateLimitations} label="Profile / Limits" accent="teal" />
            <DocItem id="hdr-toggle-letter" testId="toggle-letter" checked={letterOn}
              onChange={(checked) => {
                setGenerateAdminLetter(checked);
                if (!checked) {
                  setLetterType('');
                  if (setCustomLetterDescription) setCustomLetterDescription('');
                }
              }} label="Admin Letter" accent="yellow" />
            <DocItem id="hdr-toggle-em" testId="toggle-em-analysis" checked={includeEmAnalysis}
              onChange={setIncludeEmAnalysis} label="E/M Coding" accent="cyan" />
            {/* Mar 1, 2026 — ICD-10 quick-add INLINE at the end of the chip
                row. Provider drew this layout: one single row containing
                NOTE pills + Separate Documents chips + ICD-10 input
                filling whatever horizontal space remains. `flex-1` lets
                the input expand to consume the right-edge slack. */}
            {typeof setManualIcd10 === 'function' && (
              <div
                className="flex items-center gap-1.5 flex-1 min-w-[180px] ml-1"
                data-testid="outputs-header-icd10-wrap"
              >
                <Plus className="w-3 h-3 text-primary shrink-0" aria-hidden="true" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-foreground/75 shrink-0">
                  ICD-10
                </span>
                <Input
                  placeholder="e.g., M54.5, G43.909, F32.1 (comma-separated)"
                  value={manualIcd10 || ''}
                  onChange={(e) => setManualIcd10(e.target.value.toUpperCase())}
                  className="h-7 text-xs bg-input border-border font-mono flex-1 min-w-0"
                  data-testid="icd10-input-outputs-header"
                  aria-label="Add ICD-10 codes"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutputsHeaderRow;
